"use client"

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  db,
  type FavoriteSong,
} from "./db/db"
import { motion, AnimatePresence } from "framer-motion"
import { HOME_ANIMATION } from "@/app/pages/homePage/utils/home.constants"
import useOrientation from "@/hooks/useOrientation"
import Topnav from "@/components/topnav"
import { useMusicContext } from "@/context/musicContext"

import {
  Play,
  Pause,
  Heart,
  Grid3X3,
  List,
  Music4,
} from "lucide-react"


/* =========================
   CONFIG
========================= */

const MAX_FAVORITES = 100

/* =========================
   ANIMATION
========================= */

const container = {
  hidden: {
    opacity: 0,
    y: 12,
  },

  show: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.04,
    },
  },
}

const item = {
  hidden: {
    opacity: 0,
    y: 8,
  },

  show: {
    opacity: 1,
    y: 0,
  },
}

/* =========================
   COMPONENT
========================= */

export default function Favoritos() {
  const {
    isPortrait,
    isLandscape,
    isStandalone,
  } = useOrientation()

  const {
    playSong,
    isPlaying,
    currentTrackData,
    currentPlaylist,
    togglePlayPause,
  } = useMusicContext()

  const [favorites, setFavorites] =
    useState<FavoriteSong[]>([])

  const [view, setView] =
    useState<"grid" | "list">("list")

  /* =========================
     LOAD FAVORITES
  ========================= */
const loadFavorites = useCallback(
  async () => {
    try {
      const songs = await db.favorites.toArray()

      songs.sort(
        (a, b) =>
          new Date(b.savedAt).getTime() -
          new Date(a.savedAt).getTime()
      )

      setFavorites(songs)
    } catch (error) {
      console.error(
        "Error cargando favoritos:",
        error
      )
    }
  },
  []
)

  useEffect(() => {
    loadFavorites()
  }, [loadFavorites])


    useEffect(() => {

    const handleFavoritesChanged =
      () => {
        void loadFavorites()
      }

    window.addEventListener(
      "mixtape:favorites-changed",
      handleFavoritesChanged,
    )

    return () => {

      window.removeEventListener(
        "mixtape:favorites-changed",
        handleFavoritesChanged,
      )
    }

  }, [loadFavorites])


    /* =========================
     NATIVE FAVORITE BRIDGE
  ========================= */

  useEffect(() => {

    const handleNativeFavoriteToggle = async (
      event: Event
    ) => {

      try {

        const customEvent =
          event as CustomEvent

        const data =
          customEvent.detail

        if (!data) {
          return
        }

        const url =
          String(data.url ?? "").trim()

        if (!url) {
          console.warn(
            "⚠️ Favorito nativo sin URL"
          )

          return
        }

        const name =
          String(
            data.name ?? "Mixtape"
          )

        const artist =
          String(
            data.artist ?? ""
          )

        const songimg =
          String(
            data.songimg ?? ""
          )

        const trackTime =
          data.trackTime != null
            ? String(data.trackTime)
            : undefined

        /*
         * Comprobamos si ya existe.
         */

        const existing =
          await db.favorites.get(url)

        if (existing) {

          /*
           * Ya era favorito.
           * El botón nativo significa:
           * quitar de favoritos.
           */

          await db.favorites.delete(url)

          console.log(
            "💔 Favorito eliminado desde Native:",
            name
          )

        } else {

          /*
           * Nuevo favorito.
           */

          const count =
            await db.favorites.count()

          if (
            count >= MAX_FAVORITES
          ) {

            console.warn(
              "⚠️ Límite de favoritos alcanzado:",
              MAX_FAVORITES
            )

            return
          }

          await db.favorites.put({

            url,

            name,

            artist,

            songimg,

            trackTime,

            type:
              data.type ??
              "favorites",

            link:
              data.link ??
              url,

            originalPlaylistId:
              data.originalPlaylistId ??
              null,

            originalPlaylistTitle:
              data.originalPlaylistTitle ??
              null,

            savedAt:
              new Date(),
          })

          console.log(
            "❤️ Favorito guardado desde Native:",
            name
          )
        }

        /*
         * Refrescamos inmediatamente
         * la lista visual.
         */

        await loadFavorites()

      } catch (error) {

        console.error(
          "❌ Error procesando favorito nativo:",
          error
        )
      }
    }

    window.addEventListener(
      "nativeFavoriteToggle",
      handleNativeFavoriteToggle
    )

    return () => {

      window.removeEventListener(
        "nativeFavoriteToggle",
        handleNativeFavoriteToggle
      )
    }

  }, [loadFavorites])



  /* =========================
     FAVORITES PLAYLIST
  ========================= */
const favoritesPlaylist = useMemo(() => {
  return {
    id: "favorites",

    title: "Mis Favoritos",

    type: "favorites",

    link: "favorites",

    playlistData: favorites.map((song) => ({
      id: song.url,

      url: song.url,

      link: song.link ?? song.url,

      songName: song.name,

      songArtist: song.artist,

      songimg: song.songimg,

      trackTime: song.trackTime,

      type: "favorites",

      favorite: true,

      originalUrl: song.url,

      originalPlaylistId:
        song.originalPlaylistId,

      originalPlaylistTitle:
        song.originalPlaylistTitle,
    })),
  }
}, [favorites])

  /* =========================
     PLAY SONG
  ========================= */

  const handlePlaySong = useCallback(
    (song: FavoriteSong) => {
      const index =
        favorites.findIndex(
          (item) =>
            item.url === song.url
        )

      if (index === -1) return

      /*
       * Si ya estamos en favoritos
       * y hemos pulsado la misma canción,
       * hacemos play / pause.
       */

      const isCurrent =
        currentPlaylist?.id ===
          "favorites" &&
        currentTrackData?.trackKey?.[1] ===
          index

      if (isCurrent) {
        togglePlayPause()
        return
      }

      const track =
        favoritesPlaylist.playlistData[
          index
        ]

      if (!track) return

      playSong(
        track,
        index,
        favoritesPlaylist
      )
    },
    [
      favorites,
      favoritesPlaylist,
      currentPlaylist,
      currentTrackData,
      togglePlayPause,
      playSong,
    ]
  )

  /* =========================
     PLAY ALL
  ========================= */

  const handlePlayAll = useCallback(() => {
    if (
      !favoritesPlaylist.playlistData.length
    ) {
      return
    }

    /*
     * Si ya estamos dentro de favoritos,
     * el botón funciona como play / pause.
     */

    if (
      currentPlaylist?.id ===
      "favorites"
    ) {
      togglePlayPause()
      return
    }

    const firstTrack =
      favoritesPlaylist.playlistData[0]

    playSong(
      firstTrack,
      0,
      favoritesPlaylist
    )
  }, [
    favoritesPlaylist,
    currentPlaylist,
    togglePlayPause,
    playSong,
  ])

  /* =========================
     REMOVE FAVORITE
  ========================= */

  const removeFavorite = useCallback(
    async (url: string) => {
      try {
        const index =
          favorites.findIndex(
            (song) =>
              song.url === url
          )

        const isCurrent =
          currentPlaylist?.id ===
            "favorites" &&
          currentTrackData?.trackKey?.[1] ===
            index

        /*
         * Si eliminamos la canción
         * que está sonando, pausamos.
         */

        if (isCurrent) {
          togglePlayPause()
        }

        await db.favorites.delete(url)

        await loadFavorites()
      } catch (error) {
        console.error(
          "Error eliminando favorito:",
          error
        )
      }
    },
    [
      favorites,
      currentPlaylist,
      currentTrackData,
      togglePlayPause,
      loadFavorites,
    ]
  )

  /* =========================
     CURRENT SONG
  ========================= */

  const isCurrent = useCallback(
    (song: FavoriteSong) => {
      const index =
        favorites.findIndex(
          (item) =>
            item.url === song.url
        )

      return (
        currentPlaylist?.id ===
          "favorites" &&
        currentTrackData?.trackKey?.[1] ===
          index &&
        isPlaying
      )
    },
    [
      favorites,
      currentPlaylist,
      currentTrackData,
      isPlaying,
    ]
  )

  /* =========================
     UI STATE
  ========================= */

  const used =
    favorites.length

  const percent =
    Math.min(
      (used / MAX_FAVORITES) * 100,
      100
    )

  /* =========================
     RENDER
  ========================= */

  return (
  <div className="relative w-full h-[100dvh] overflow-hidden overscroll-none touch-pan-y">
    <Topnav />

    <div
      className={`
        inset-0 flex w-full flex-col select-none
        ${isLandscape ? "h-[calc(100%-3.7rem)] rounded-xl border border-white/15 bg-[var(--movil-nav)]" : ""}
        ${isPortrait ? "h-[100dvh] pb-0 pt-12" : ""}
        ${isStandalone ? "h-[100dvh] mb-40" : ""}
      `}
    >
      <div
        className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto scrollbar-hide"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "none",
          overscrollBehaviorX: "none",
        }}
      >
        {/* HEADER */}
        <motion.div
          variants={HOME_ANIMATION}
          initial="initial"
          animate="animate"
          exit="exit"
          className={`motion-content w-full px-4 pt-3 select-none ${
            isStandalone ? "mt-18" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-lg font-semibold">
                <Heart size={18} className="fill-current" />
                Mis Favoritos
              </h1>

              <p className="text-xs opacity-50">
                Tus canciones favoritas en un solo lugar
              </p>
            </div>

            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => setView("list")}
                className={`rounded-lg p-2 transition ${
                  view === "list"
                    ? "border border-[var(--soft-border)] bg-[var(--soft-bg)]"
                    : "bg-white/5"
                }`}
              >
                <List size={16} />
              </button>

              <button
                onClick={() => setView("grid")}
                className={`rounded-lg p-2 transition ${
                  view === "grid"
                    ? "border border-[var(--soft-border)] bg-[var(--soft-bg)]"
                    : "bg-white/5"
                }`}
              >
                <Grid3X3 size={16} />
              </button>
            </div>
          </div>

          {/* STORAGE */}
          <div className="mt-4 rounded-xl border border-[var(--soft-border)] bg-[var(--soft-bg)] p-3">
            <div className="mb-1 flex justify-between text-xs opacity-50">
              <span>Favoritos</span>
              <span>
                {used}/{MAX_FAVORITES}
              </span>
            </div>

            <div className="h-2 overflow-hidden rounded-full border border-[var(--soft-border)] bg-[var(--soft-bg)]">
              <div
                className="h-full bg-gradient-to-r from-pink-400 to-rose-500 transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          {/* PLAY ALL */}
          <button
            onClick={handlePlayAll}
            disabled={!favorites.length}
            className="mt-3 w-full rounded-xl border border-[var(--soft-border)] bg-[var(--primary)]/40 py-2 font-medium text-[var(--primary-color)] transition active:scale-95 disabled:opacity-60"
          >
            {currentPlaylist?.id === "favorites" && isPlaying
              ? "Pausar favoritos"
              : "Reproducir favoritos"}
          </button>
        </motion.div>

        {/* LIST */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="motion-content w-full p-4"
        >
          {favorites.length === 0 ? (
            <div className="mt-14 flex flex-col items-center justify-center gap-3 text-center opacity-40">
              <Heart size={32} strokeWidth={1.5} />

              <div>
                <p className="text-sm font-medium">
                  No tienes favoritos
                </p>

                <p className="mt-1 text-xs">
                  Marca canciones con ❤️ para verlas aquí
                </p>
              </div>
            </div>
          ) : (
            <div
              className={
                view === "grid"
                  ? "grid grid-cols-2 gap-3"
                  : "flex flex-col gap-3"
              }
            >
              <AnimatePresence initial={false}>
                {favorites.map((song) => {
                  const current = isCurrent(song)

                  return (
                    <motion.div
                      key={song.url}
                      variants={item}
                      layout
                      exit={{ opacity: 0, scale: 0.95 }}
                      className={`
                        rounded-2xl border border-[var(--soft-border)]
                        bg-[var(--soft-bg)] p-3 backdrop-blur-md
                        ${view === "grid" ? "" : "flex items-center gap-3"}
                      `}
                    >
                      {/* IMAGE */}
                      <div className="relative shrink-0">
                        <img
                          src={song.songimg || "/placeholder.png"}
                          alt={song.name || "Canción"}
                          className="h-14 w-14 rounded-xl object-cover"
                        />

                        {current && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40">
                            {isPlaying ? (
                              <Pause size={18} />
                            ) : (
                              <Play size={18} />
                            )}
                          </div>
                        )}
                      </div>

                      {/* INFO */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {song.name}
                        </p>

                        <p className="truncate text-xs opacity-60">
                          {song.artist || "Artista desconocido"}
                        </p>

                        {song.originalPlaylistTitle && (
                          <p className="mt-1 truncate text-[10px] opacity-30">
                            {song.originalPlaylistTitle}
                          </p>
                        )}
                      </div>

                      {/* ACTIONS */}
                      <div className="flex shrink-0 gap-2">
                        <button
                          onClick={() => handlePlaySong(song)}
                          aria-label={current ? "Pausar" : "Reproducir"}
                          className="rounded-full border border-[var(--soft-border)] bg-[var(--soft-bg)] p-2 transition active:scale-90"
                        >
                          {current && isPlaying ? (
                            <Pause size={16} />
                          ) : (
                            <Play size={16} />
                          )}
                        </button>

                        <button
                          onClick={() => removeFavorite(song.url)}
                          aria-label="Eliminar favorito"
                          className="rounded-full border border-[var(--soft-border)] bg-pink-500/10 p-2 text-pink-400 transition active:scale-90"
                        >
                          <Heart size={16} className="fill-current" />
                        </button>
                      </div>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  </div>
)
}